import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PanelLayoutGate from "@/obihub-os/runtime/PanelLayoutGate";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import OnboardingPage from "@/pages/OnboardingPage";
import ParasutAuthCallbackPage from "@/pages/ParasutAuthCallbackPage";
import DashboardPage from "@/pages/DashboardPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import IntegrationSettingsPage from "@/pages/IntegrationSettingsPage";
import IntegrationOrdersPage from "@/pages/IntegrationOrdersPage";
import OrdersPage from "@/pages/OrdersPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ProductSettingsPage from "@/pages/ProductSettingsPage";
import AIIntelligencePage from "@/pages/AIIntelligencePage";
import CategoriesPage from "@/pages/CategoriesPage";
import StockPage from "@/pages/StockPage";
import ReportsPage from "@/pages/ReportsPage";
import CustomersPage from "@/pages/CustomersPage";
import SettingsPage from "@/pages/SettingsPage";
import LogsPage from "@/pages/LogsPage";
import MyCompaniesPage from "@/pages/MyCompaniesPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceDetailPage from "@/pages/InvoiceDetailPage";
import TekliflerPage from "@/pages/TekliflerPage";
import ProductStockListPage from "@/pages/ProductStockListPage";
import ProductPriceListPage from "@/pages/ProductPriceListPage";
import IntegrationJobsPage from "@/pages/IntegrationJobsPage";
import ExpenseInvoicesPage from "@/pages/ExpenseInvoicesPage";
import IncomingEInvoicesPage from "@/pages/IncomingEInvoicesPage";
import HepsiJetSettingsPage from "@/pages/HepsiJetSettingsPage";
import YurticiSettingsPage from "@/pages/YurticiSettingsPage";
import HepsiJetManualShipmentsPage from "@/pages/HepsiJetManualShipmentsPage";
import InviteEarnPage from "@/pages/InviteEarnPage";
import BillingPage from "@/pages/BillingPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import ApplicationsPage from "@/pages/ApplicationsPage";
import ApplicationDetailPage from "@/pages/ApplicationDetailPage";
import SupportTicketsPage from "@/pages/SupportTicketsPage";
import SuperLoginPage from "@/pages/SuperLoginPage";
import SuperPanelPage from "@/pages/SuperPanelPage";
import POSPage from "@/pages/POSPage";
import ObiHubInvoicesPage, { ObiHubInvoiceFormPage } from "@/pages/ObiHubInvoicesPage";
import KasaPage from "@/pages/KasaPage";
import FinancePage from "@/pages/FinancePage";
import IntegrationProductMatchingPage from "@/pages/IntegrationProductMatchingPage";
import { canAccessPath, getDefaultPanelPath } from "@/lib/authz";
import { api } from "@/lib/api";
import { ClientErrorMonitor } from "@/lib/errorMonitor";
import { installSafeToast } from "@/lib/toastSafe";
import AppErrorBoundary from "@/components/common/AppErrorBoundary";
import "@/App.css";

installSafeToast();

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/giris" replace />;
  }

  if (!canAccessPath(user, location.pathname)) {
    return <Navigate to={getDefaultPanelPath(user)} replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={getDefaultPanelPath(user)} replace />;
  }
  
  return children;
};

const SuperPublicRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const token = localStorage.getItem("obihub_super_token");
      if (!token) {
        if (mounted) {
          setAuthed(false);
          setLoading(false);
        }
        return;
      }
      try {
        await api.get("/auth/super-me");
        if (mounted) setAuthed(true);
      } catch {
        localStorage.removeItem("obihub_super_token");
        if (mounted) setAuthed(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400">
        Yükleniyor...
      </div>
    );
  }

  if (authed) {
    return <Navigate to="/super/panel" replace />;
  }

  return children;
};

const SuperProtectedRoute = ({ component: Component }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.get("/auth/super-me");
        if (mounted) setProfile(data);
      } catch {
        localStorage.removeItem("obihub_super_token");
        if (mounted) setProfile(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400">
        Yükleniyor...
      </div>
    );
  }

  if (!profile) {
    return <Navigate to="/super-giris" replace />;
  }

  return <Component profile={profile} />;
};

function AppRoutes() {
  const panelRouteChildren = (
    <>
      <Route index element={<DashboardPage />} />
      <Route path="entegrasyonlar" element={<IntegrationsPage />} />
      <Route path="entegrasyonlar/:id/ayarlar" element={<IntegrationSettingsPage />} />
      <Route path="entegrasyonlar/:id/siparisler" element={<IntegrationOrdersPage />} />
      <Route path="entegrasyonlar/:id/urun-eslestirme" element={<IntegrationProductMatchingPage />} />
      <Route path="siparisler" element={<OrdersPage />} />
      <Route path="urunler" element={<ProductsPage />} />
      <Route path="urunler/ayarlar" element={<ProductSettingsPage />} />
      <Route path="ai-intelligence" element={<AIIntelligencePage />} />
      <Route path="kategoriler" element={<CategoriesPage />} />
      <Route path="urunler/:id" element={<ProductDetailPage />} />
      <Route path="stok" element={<StockPage />} />
      <Route path="raporlar" element={<ReportsPage />} />
      <Route path="musteriler" element={<CustomersPage />} />
      <Route path="ayarlar" element={<SettingsPage />} />
      <Route path="hesabim/firmalarim" element={<MyCompaniesPage />} />
      <Route path="hesabim/davet-et-kazan" element={<InviteEarnPage />} />
      <Route path="hesabim/faturalandirma" element={<BillingPage />} />
      <Route path="hesabim/abonelik" element={<SubscriptionsPage />} />
      <Route path="hesabim/destek-taleplerim" element={<SupportTicketsPage />} />
      <Route path="uygulamalar" element={<ApplicationsPage />} />
      <Route path="uygulamalar/:slug" element={<ApplicationDetailPage />} />
      <Route path="loglar" element={<LogsPage />} />
      <Route path="urun-stok" element={<ProductStockListPage />} />
      <Route path="urun-fiyat" element={<ProductPriceListPage />} />
      <Route path="urunler/islemler" element={<IntegrationJobsPage />} />
      <Route path="satis" element={<POSPage />} />
      <Route path="kasa" element={<KasaPage />} />
      <Route path="finans" element={<FinancePage />} />
      <Route path="satislar/fatura-olustur" element={<ObiHubInvoicesPage />} />
      <Route path="satislar/fatura-olustur/yeni" element={<ObiHubInvoiceFormPage />} />
      <Route path="satislar/fatura-olustur/:id" element={<ObiHubInvoiceFormPage />} />
      <Route path="satislar/faturalar" element={<InvoicesPage />} />
      <Route path="satislar/faturalar/:id" element={<InvoiceDetailPage />} />
      <Route path="satislar/teklifler" element={<TekliflerPage />} />
      <Route path="giderler/faturalar" element={<ExpenseInvoicesPage />} />
      <Route path="giderler/gelen-e-faturalar" element={<IncomingEInvoicesPage />} />
      <Route path="kargo/hepsijet" element={<HepsiJetSettingsPage />} />
      <Route path="kargo/yurtici" element={<YurticiSettingsPage />} />
      <Route path="operasyon/hepsijet-etiket" element={<HepsiJetManualShipmentsPage />} />
    </>
  );

  return (
    <Routes>
      <Route
        path="/super-giris"
        element={
          <SuperPublicRoute>
            <SuperLoginPage />
          </SuperPublicRoute>
        }
      />
      <Route path="/super/panel" element={<SuperProtectedRoute component={SuperPanelPage} />} />

      {/* Public Routes */}
      <Route path="/giris" element={
        <PublicRoute><LoginPage /></PublicRoute>
      } />
      <Route path="/kayit" element={
        <PublicRoute><RegisterPage /></PublicRoute>
      } />
      <Route path="/sifremi-unuttum" element={
        <PublicRoute><ForgotPasswordPage /></PublicRoute>
      } />
      <Route path="/auth/parasut/callback" element={<ParasutAuthCallbackPage />} />
      <Route path="/onboarding" element={
        <ProtectedRoute><OnboardingPage /></ProtectedRoute>
      } />


      {/* Protected Routes */}
      <Route path="/panel" element={
        <ProtectedRoute><PanelLayoutGate /></ProtectedRoute>
      }>
        {panelRouteChildren}
      </Route>
      <Route path="/:tenant" element={
        <ProtectedRoute><PanelLayoutGate /></ProtectedRoute>
      }>
        {panelRouteChildren}
      </Route>
      
      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/giris" replace />} />
      <Route path="*" element={<Navigate to="/panel" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <BrowserRouter>
      <AuthProvider>
        <AppErrorBoundary>
          <ClientErrorMonitor />
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            expand
            visibleToasts={5}
            offset={20}
            toastOptions={{
              duration: 4000,
              style: { zIndex: 99999 },
            }}
          />
        </AppErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
